import { createSlice } from '@reduxjs/toolkit';
import { createTransform, persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import { notificationReceived } from '../notifications/notificationsSlice';
import { parseChannel } from '../notifications/notificationsUtils';
import { fetchDashboard } from './dashboardThunks';
import { DASHBOARD_CHANNEL } from './dashboardConstants';
import { getDefaultRange, restoreFilters } from './dashboardUtils';

/**
 * The control tower.
 *
 * The filters survive a reload, like every other listing, under a key of their
 * own — the legacy keeps the dashboard and the deliveries table in one
 * `localStorage` entry and lets them overwrite each other.
 *
 * The window needs one distinction the other screens do not make: whether the
 * user chose it. `restoreFilters` is where that is decided.
 */
const initialState = {
  kpis: null,
  previous: null,
  range: null,
  statusCount: [],
  courierCount: [],
  activity: [],
  daily: [],
  communeCount: [],
  companyCount: [],
  needsAttention: [],
  filters: {
    ...getDefaultRange(),
    company: 'all',
    /** Which operator the numbers are about. Staff only; see `fetchDashboard`. */
    admin_id: 'all',
    isDefaultRange: true,
  },
  isLoading: false,
  error: null,
  /** Something moved on the backend and these numbers are behind. */
  isStale: false,
  lastFetchedAt: null,
  /**
   * The latest request. One that answers after it — a slow one cut by the
   * gateway a minute later, say — must not paint over what came after.
   */
  requestId: null,
};

const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState,
  reducers: {
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };

      // Any change that carries a date is the user answering with a window of
      // their own, and from then on it is theirs to keep.
      if ('start_date' in action.payload || 'end_date' in action.payload) {
        state.filters.isDefaultRange = false;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDashboard.pending, (state, action) => {
        state.isLoading = true;
        state.error = null;
        state.requestId = action.meta.requestId;
      })
      .addCase(fetchDashboard.fulfilled, (state, action) => {
        if (state.requestId && action.meta.requestId !== state.requestId) return;
        state.isLoading = false;
        state.kpis = action.payload.kpis || null;
        state.previous = action.payload.previous || null;
        state.range = action.payload.range || null;
        state.statusCount = action.payload.status_count || [];
        state.courierCount = action.payload.courier_count || [];
        state.activity = action.payload.recent_activity || [];
        state.daily = action.payload.daily_count || [];
        state.communeCount = action.payload.commune_count || [];
        state.companyCount = action.payload.company_count || [];
        state.needsAttention = action.payload.needs_attention || [];
        state.isStale = false;
        state.lastFetchedAt = Date.now();
      })
      .addCase(fetchDashboard.rejected, (state, action) => {
        if (state.requestId && action.meta.requestId !== state.requestId) return;
        state.isLoading = false;
        state.error = action.payload;
        // Both of these matter, and leaving them out was a tight retry loop: the
        // screen refetches while `isStale` is set and enough time has passed,
        // and a rejection that touched neither flag left `isStale` true with
        // `lastFetchedAt` still null — so "enough time" was always true and the
        // next render dispatched again, as fast as the requests could fail.
        // A failure now waits for the button or the next socket notice.
        state.isStale = false;
        state.lastFetchedAt = Date.now();
      })
      // The socket event the Centro de Actividad ignores. One action, two
      // reducers: the panel skips it because it is not news the user asked for,
      // and this slice reads it because it means the numbers moved.
      .addCase(notificationReceived, (state, action) => {
        const { channel, role, userId } = action.payload;
        if (parseChannel(channel, role, userId) === DASHBOARD_CHANNEL) {
          state.isStale = true;
        }
      });
  },
});

export const { setFilters } = dashboardSlice.actions;

/** Unwrapped, so a test can drive it without going through the persistence. */
export const dashboardReducer = dashboardSlice.reducer;

/**
 * The repair runs here rather than in a `REHYDRATE` case of the reducer, and
 * that is not a detail: `autoMergeLevel1` drops any key the reducer touched
 * while handling that same action, so fixing the window there threw the whole
 * stored `filters` away — filters that then got saved back over themselves as
 * the defaults. A transform runs before the merge, on the way out of storage.
 */
const restoreTransform = createTransform(
  (inboundState) => inboundState,
  (outboundState, key) => (key === 'filters' ? restoreFilters(outboundState) : outboundState),
  { whitelist: ['filters'] }
);

/** Its own `localStorage` key, never one shared with another screen. */
const persistConfig = {
  key: 'dashboardFilter',
  storage,
  whitelist: ['filters'],
  transforms: [restoreTransform],
};

export default persistReducer(persistConfig, dashboardSlice.reducer);
